import { Injectable } from '@angular/core';
import { Tarifa } from '../../domain/tarifa.domain';
import { OrdenItem } from '../../domain/orden.domain';
import { PricingCalculator } from './pricing-calculator';
import { PricingDebugLogger } from './pricing-debug-logger';
import { TarifaDetalleMatchEngine } from './tarifa-detalle-match-engine';
import { PricingStatus, PricingTrace } from './pricing-trace.model';

export interface PricingPreviewResult {
  subtotalPreview: number;
  duracionSegundosPreview: number;
  tarifaDetalleId: number | null;
  unidad: string;
  precioUnitario: number;
  status: PricingStatus;
  trace: PricingTrace;
  errorMessage: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class ClientPricingService {
  private readonly matchEngine = new TarifaDetalleMatchEngine();
  private readonly calculator = new PricingCalculator();
  private readonly logger = new PricingDebugLogger();

  calculateItem(item: Partial<OrdenItem>, tarifa: Tarifa): PricingPreviewResult {
    // LOG: Inicio del cálculo de pricing (FRONTEND)
    console.group('[CLIENT_PRICING_FRONTEND] Inicio cálculo');
    console.log('Inputs:', {
      tarifa_id: tarifa.id,
      tarifa_codigo: tarifa.codigo,
      tarifa_nombre: tarifa.nombre,
      tarifa_detalles_count: tarifa.tarifaDetalles?.length ?? 0,
      item_tipo_producto_id: this.extractId(item.tipoProducto),
    });
    console.groupEnd();

    if (!tarifa) {
      console.error('[CLIENT_PRICING_FRONTEND] Error: Tarifa no especificada');
      return this.createErrorResult('Tarifa no especificada', 'INCOMPLETE');
    }

    if (!tarifa.tarifaDetalles || tarifa.tarifaDetalles.length === 0) {
      console.error('[CLIENT_PRICING_FRONTEND] Error: Tarifario sin detalles configurados');
      return this.createErrorResult('Tarifario sin detalles configurados', 'NO_MATCH');
    }

    if (!item.tipoProducto) {
      console.error('[CLIENT_PRICING_FRONTEND] Error: Item sin tipo de producto');
      return this.createErrorResult('Item sin tipo de producto', 'INCOMPLETE');
    }

    const matchResult = this.matchEngine.match(item, tarifa.tarifaDetalles, {
      id: tarifa.id ?? undefined,
      codigo: tarifa.codigo ?? undefined,
      nombre: tarifa.nombre ?? undefined
    });

    if (!matchResult.matched) {
      const message = matchResult.trace.message || 'No se encontró tarifa aplicable.';
      console.error('[CLIENT_PRICING_FRONTEND] Error: No se encontró match', {
        message,
        status: matchResult.trace.status,
        rejections_count: matchResult.trace.match.rejections?.length ?? 0,
      });
      return this.createErrorResult(message, matchResult.trace.status, matchResult.trace);
    }

    const calcResult = this.calculator.calculate(item, matchResult.matched);
    const status: PricingStatus = calcResult.trace.bonificado ? 'BONIFICADO' : matchResult.trace.status;
    const mergedTrace: PricingTrace = {
      ...matchResult.trace,
      status,
      calculation: calcResult.trace,
      matchedDetalle: matchResult.matched
    };

    const result: PricingPreviewResult = {
      subtotalPreview: calcResult.subtotalPreview,
      duracionSegundosPreview: calcResult.duracionSegundosPreview,
      tarifaDetalleId: matchResult.matched.id ?? null,
      unidad: calcResult.unidadTarifacion,
      precioUnitario: calcResult.precioUnitario,
      status,
      trace: mergedTrace,
      errorMessage: calcResult.errorMessage ?? null
    };

    // LOG: Resultado final del cálculo (FRONTEND)
    console.group('[CLIENT_PRICING_FRONTEND] Cálculo completado');
    console.log('Tarifa Detalle Seleccionada:', {
      id: matchResult.matched.id,
      prioridad: matchResult.matched.prioridad ?? 0,
      precio_unitario: calcResult.precioUnitario,
      unidad: calcResult.unidadTarifacion,
    });
    console.log('Cálculo:', {
      cantidad_facturable: calcResult.trace.billingQty,
      factor: calcResult.trace.factor,
      subtotal_preview: calcResult.subtotalPreview,
      bonificado: calcResult.trace.bonificado,
    });
    console.log('Resultado completo:', {
      tarifa_detalle_id: result.tarifaDetalleId,
      subtotal_preview: result.subtotalPreview,
      status: result.status,
    });
    console.groupEnd();

    this.logger.logItem(item, mergedTrace, matchResult.matched);

    return result;
  }

  private extractId(value: any): number | null {
    if (value === null || value === undefined) {
      return null;
    }
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'object' && value.id) {
      return Number(value.id);
    }
    if (typeof value === 'object' && value['@id']) {
      const match = String(value['@id']).match(/\/(\d+)$/);
      return match ? Number(match[1]) : null;
    }
    if (typeof value === 'string') {
      const match = value.match(/\/(\d+)$/);
      return match ? Number(match[1]) : null;
    }
    return null;
  }

  private createErrorResult(
    errorMessage: string,
    status: PricingStatus,
    trace?: PricingTrace
  ): PricingPreviewResult {
    const fallbackTrace: PricingTrace = trace ?? {
      status,
      message: errorMessage,
      match: {
        inputs: {},
        candidates: [],
        rejections: [],
        selectedDetalle: null
      }
    };

    return {
      subtotalPreview: 0,
      duracionSegundosPreview: 0,
      tarifaDetalleId: null,
      unidad: '',
      precioUnitario: 0,
      status,
      trace: fallbackTrace,
      errorMessage
    };
  }
}
